import { SalaryType } from "../../shared/util/salary/SalaryType";

import { useCallback } from "react";

export const payPeriodToShortStringMapping = (salaryType?: SalaryType): string => {
  const mapping = {
    [SalaryType.Hourly]: "hr" as const,
    [SalaryType.Daily]: "day" as const,
    [SalaryType.Weekly]: "wk" as const,
    [SalaryType.Monthly]: "mo" as const,
    [SalaryType.Yearly]: "yr" as const,
  };
  return salaryType && mapping[salaryType] ? mapping[salaryType] : "hr";
};

/**
 * Map SalaryType enum to string literal type for modal display
 */
const salaryTypeToStringMapping = {
  [SalaryType.Hourly]: "hourly" as const,
  [SalaryType.Daily]: "daily" as const,
  [SalaryType.Weekly]: "weekly" as const,
  [SalaryType.Monthly]: "monthly" as const,
  [SalaryType.Yearly]: "yearly" as const,
};

/**
 * Map string payPeriod back to SalaryType enum
 */
const stringToSalaryTypeMapping = {
  hourly: SalaryType.Hourly,
  daily: SalaryType.Daily,
  weekly: SalaryType.Weekly,
  monthly: SalaryType.Monthly,
  yearly: SalaryType.Yearly,
};

/**
 * Convert SalaryType enum to string literal type
 */
export const convertSalaryTypeToString = (
  salaryType: SalaryType,
): "hourly" | "daily" | "weekly" | "monthly" | "yearly" => {
  return salaryTypeToStringMapping[salaryType];
};

/**
 * Convert string literal type to SalaryType enum
 */
export const convertStringToSalaryType = (
  payPeriod: "hourly" | "daily" | "weekly" | "monthly" | "yearly",
): SalaryType => {
  return stringToSalaryTypeMapping[payPeriod];
};

/**
 * Safely convert a string to SalaryType enum, returning undefined if not supported
 */
const convertToSalaryType = (salaryType: string): SalaryType | undefined => {
  const supportedTypes = Object.values(SalaryType) as string[];
  return supportedTypes.includes(salaryType) ? (salaryType as SalaryType) : undefined;
};

/**
 * Hook for converting GraphQL salary type data to local NumericPayPreferenceData format
 */
export const useConvertSalaryTypeData = () => {
  return useCallback((amount?: number | null, salaryType?: string | null) => {
    if (amount === null || amount === undefined || amount === 0 || !salaryType) {
      return undefined;
    }

    const convertedSalaryType = convertToSalaryType(salaryType);
    if (!convertedSalaryType) {
      return undefined;
    }

    return {
      minimumPay: amount,
      payPeriod: convertedSalaryType,
    };
  }, []);
};
